import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ColourSwatches from "./ColourSwatches";
import { COLOURS, getColour, DEFAULT_COLOUR_KEY } from "../data/colours";
import {
  APPLICATIONS,
  PROFILE_OPTIONS,
  GLAZING_OPTIONS,
  LOCK_OPTIONS,
  handleTypesFor,
  handleDetailsFor,
  lockLatchTypesFor,
  ELECTRONIC_LOCK_MODELS,
  HARDWARE_CATALOGUE,
  HARDWARE_PULLS,
  hardwareOptionsFor,
  isPullHardware,
  FIXING_OPTIONS,
  THRESHOLD_OPTIONS,
  OPENING_OPTIONS,
  HANDING_OPTIONS,
  needsHanding,
  LUXURY_PANEL_CONSTRUCTION,
  isExternalProfile,
  profilesFor,
  defaultSpec,
} from "../data/productSpecs";
import {
  GlassPicker,
  SwingPicker,
  LabelWithHelp,
} from "./SpecPickers";
import ProfileHelper from "./ProfileHelper";
import FixingHelper from "./FixingHelper";
import ModalCustomNotice from "./ModalCustomNotice";

const ease = [0.19, 1, 0.22, 1];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const GLASS_OPTIONS = GLAZING_OPTIONS;

const initialForm = {
  email: "",
  phone: "",
  width_mm: "",
  height_mm: "",
  glass_type: "Double Glazed",
  custom_colour: "",
  quantity: "1",
  notes: "",
  application: "",
  profile: "",
  opening: "",
  handing: "",
  hardware: "Lever Handle",
  handle_detail: "Standard",
  handle_custom_model: "",
  hardware_size_mm: "",
  lock: "Lock Cylinder",
  lock_other: "",
  electronic_lock: "None — Standard Lock Only",
  fixing: "",
  threshold: "",
};

/**
 * Product detail modal.
 * - Image carousel across rest / closed / open (whichever exist for the product)
 * - Placeholder description block
 * - Quote request form -> POST /api/quotes
 * - Dulux Powdercoat colour swatches — retint the frame image; selection syncs
 *   both directions with the product card via `initialColourKey` + `onColourChange`.
 */
export default function ProductModal({
  product,
  onClose,
  initialColourKey,
  onColourChange,
}) {
  const tintable =
    product?.categoryKey === "steel-doors" ||
    product?.categoryKey === "steel-windows";
  const isHardware = product?.categoryKey === "hardware";
  const isLuxury = product?.categoryKey === "luxury-entrances";
  const images = useMemo(() => {
    if (!product) return [];
    const list = [];
    if (product.restSrc) list.push({ src: product.restSrc, label: "Frame" });
    if (product.src) list.push({ src: product.src, label: "Closed" });
    if (product.hoverSrc) list.push({ src: product.hoverSrc, label: "Open" });
    return list;
  }, [product]);

  const [idx, setIdx] = useState(0);
  const [colourKey, setColourKey] = useState(
    initialColourKey || DEFAULT_COLOUR_KEY
  );
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [showMeasuringGuide, setShowMeasuringGuide] = useState(false);
  const [showProfileHelper, setShowProfileHelper] = useState(false);
  const [showFixingHelper, setShowFixingHelper] = useState(false);
  const activeColour = getColour(colourKey);

  // Config options for this category
  const catKey = product?.categoryKey;
  const openingOpts = OPENING_OPTIONS[catKey] || [];
  const hardwareOpts = hardwareOptionsFor(catKey);
  const showConfig = !isHardware && product;
  const isExternal = form.application === "External";
  const filteredProfiles = form.application
    ? profilesFor(form.application)
    : PROFILE_OPTIONS;
  const requiresHanding = needsHanding(form.opening);
  const isPull = isPullHardware(form.hardware, catKey);

  // Reset each time modal opens for a new product
  useEffect(() => {
    if (product) {
      setIdx(0);
      const spec = defaultSpec(product) || {};
      const isLuxEntry = product.categoryKey === "luxury-entrances";
      setForm({
        ...initialForm,
        // Luxury Entrances are always external one-of-one bespoke doors.
        application: isLuxEntry ? "External" : spec.application || "",
        profile: spec.profile || "",
        // Luxury Entrances: default to Hinge + Inswing Left + Key Lock
        opening: isLuxEntry ? "Hinge" : spec.opening || "",
        handing: isLuxEntry ? "Inswing Left" : spec.handing || "",
        hardware: isLuxEntry ? "" : "Lever Handle",
        handle_detail: isLuxEntry ? "" : "Standard",
        lock: isLuxEntry ? "Key Lock" : "Lock Cylinder",
        electronic_lock: "None — Standard Lock Only",
        fixing: isLuxEntry ? "" : spec.fixing || "",
        threshold: isLuxEntry ? "" : spec.threshold || "",
        glass_type:
          isLuxEntry
            ? "Solid Steel Panel"
            : spec.glazing && GLAZING_OPTIONS.includes(spec.glazing)
              ? spec.glazing
              : "Double Glazed",
      });
      setSubmitted(false);
      setError(null);
      setColourKey(initialColourKey || DEFAULT_COLOUR_KEY);
    }
  }, [product?.code]);

  // If user switches application, keep the selected profile only if it stays valid.
  useEffect(() => {
    if (!form.application) return;
    const validNow = profilesFor(form.application).some(
      (p) => p.value === form.profile,
    );
    if (!validNow) {
      setForm((f) => ({
        ...f,
        profile: profilesFor(f.application)[0]?.value || "",
        threshold: f.application === "External" ? THRESHOLD_OPTIONS[0] : "",
      }));
    }
  }, [form.application]);

  // If user switches to a non-swing opening, clear handing.
  useEffect(() => {
    if (!needsHanding(form.opening) && form.handing) {
      setForm((f) => ({ ...f, handing: "" }));
    }
  }, [form.opening]);

  // Lock scroll while open. `overflow: hidden` isn't enough in every browser —
  // fixing the body at its current scroll position is the bulletproof pattern.
  useEffect(() => {
    if (!product) return;
    const scrollY = window.scrollY;
    const body = document.body;
    const prevPos = body.style.position;
    const prevTop = body.style.top;
    const prevWidth = body.style.width;
    const prevOverflow = body.style.overflow;
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = prevPos;
      body.style.top = prevTop;
      body.style.width = prevWidth;
      body.style.overflow = prevOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [product]);

  // Wheel scroll — no-op now that the backdrop is the sole scrolling surface.
  // Left column is sticky, right pane has natural height; native scroll works
  // regardless of where the pointer sits.

  // Escape to close
  useEffect(() => {
    if (!product) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % Math.max(images.length, 1));
      if (e.key === "ArrowLeft")
        setIdx((i) => (i - 1 + images.length) % Math.max(images.length, 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [product, images.length, onClose]);

  const updateField = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    if (!form.email.trim()) return "Please enter your email.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return "Please enter a valid email.";
    if (isHardware) {
      const q = parseInt(form.quantity, 10);
      if (!q || q <= 0) return "Please enter a quantity.";
      return null;
    }
    const w = parseInt(form.width_mm, 10);
    const h = parseInt(form.height_mm, 10);
    if (!w || w <= 0) return "Please enter a width in millimetres.";
    if (!h || h <= 0) return "Please enter a height in millimetres.";
    if (tintable && activeColour.label === "Custom" && !form.custom_colour.trim())
      return "Please describe your custom colour (e.g. RAL 7016).";
    return null;
  };

  const handleColourPick = (k) => {
    setColourKey(k);
    onColourChange?.(k);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }
    setSubmitting(true);
    try {
      const productImageUrl = product.restSrc || product.src || null;
      const payload = isHardware
        ? {
            product_code: product.code,
            product_name: product.name || null,
            product_image_url: productImageUrl,
            email: form.email.trim(),
            phone: form.phone.trim() || null,
            quantity: parseInt(form.quantity, 10),
            notes: form.notes.trim() || null,
          }
        : isLuxury
          ? {
              // Luxury Entrances are one-of-one bespoke — minimal spec: just the
              // hinge/pivot action, swing direction, lock preference, size and contact.
              product_code: product.code,
              product_name: product.name || null,
              product_image_url: productImageUrl,
              email: form.email.trim(),
              phone: form.phone.trim() || null,
              width_mm: parseInt(form.width_mm, 10),
              height_mm: parseInt(form.height_mm, 10),
              glass_type: "Solid Steel Panel",
              opening: form.opening || null,
              handing: form.opening === "Hinge" ? form.handing || null : null,
              lock: form.lock || null,
              panel: LUXURY_PANEL_CONSTRUCTION,
              notes: form.notes.trim() || null,
            }
          : {
            product_code: product.code,
            product_name: product.name || null,
            product_image_url: productImageUrl,
            email: form.email.trim(),
            phone: form.phone.trim() || null,
            width_mm: parseInt(form.width_mm, 10),
            height_mm: parseInt(form.height_mm, 10),
            glass_type: form.glass_type,
            profile: form.profile || null,
            application: form.application || null,
            opening: form.opening || null,
            handing: requiresHanding ? form.handing || null : null,
            hardware: form.hardware || null,
            handle_detail: form.handle_detail || null,
            handle_custom_model: form.handle_custom_model?.trim() || null,
            hardware_size_mm:
              form.handle_detail && /^\d+mm$/.test(form.handle_detail)
                ? form.handle_detail
                : null,
            lock: form.lock || null,
            lock_other:
              form.lock === "Other (specify)" && form.lock_other.trim()
                ? form.lock_other.trim()
                : null,
            electronic_lock:
              form.electronic_lock && form.electronic_lock !== "None — Standard Lock Only"
                ? form.electronic_lock
                : null,
            fixing: form.fixing || null,
            threshold: isExternal ? form.threshold || null : null,
            panel:
              catKey === "luxury-entrances" ? LUXURY_PANEL_CONSTRUCTION : null,
            ...(tintable
              ? {
                  colour: activeColour.label,
                  custom_colour:
                    activeColour.label === "Custom"
                      ? form.custom_colour.trim()
                      : null,
                }
              : {}),
            notes: form.notes.trim() || null,
          };
      await axios.post(`${BACKEND}/api/quotes`, payload);
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          data-testid="product-modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease }}
          onClick={onClose}
          onWheel={(e) => {
            // Some browsers won't scroll the backdrop natively when the wheel
            // target is a child element (input/select/motion.div). Force it.
            e.currentTarget.scrollTop += e.deltaY;
          }}
          className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto overscroll-contain bg-[#0a0a0c]/70 px-0 pb-[env(safe-area-inset-bottom)] pt-0 backdrop-blur-md sm:px-3 sm:py-6 lg:px-10 lg:py-10"
        >
          <motion.div
            data-testid="product-modal"
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.55, ease }}
            onClick={(e) => e.stopPropagation()}
          className="relative flex w-full max-w-[1200px] flex-col rounded-t-2xl bg-white text-[#0a0a0c] shadow-2xl ring-1 ring-[#c6a87c]/25 sm:rounded-none lg:flex-row"
        >
          {/* Close */}
          <button
            type="button"
            data-testid="product-modal-close"
            aria-label="Close product"
            onClick={onClose}
            className="absolute right-4 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white/85 text-[#0a0a0c] shadow-md backdrop-blur transition-all duration-300 hover:bg-[#0a0a0c] hover:text-white sm:top-4 sm:h-10 sm:w-10 sm:shadow-none"
            style={{ top: "calc(1rem + env(safe-area-inset-top, 0px))" }}
          >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                <path
                  d="M4 4l16 16M20 4L4 20"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            {/* Left: image carousel — sticky on desktop so it stays visible while form scrolls */}
            <div className="relative flex-1 border-b border-[#0a0a0c]/8 bg-white lg:sticky lg:top-0 lg:h-[92vh] lg:flex-shrink-0 lg:self-start lg:border-b-0 lg:border-r">
              <div
                data-testid="product-modal-carousel"
                className="relative mx-auto aspect-[3/4] w-full max-w-[520px] overflow-hidden"
              >
                {images.map((img, i) => {
                  const shouldTint = tintable && img.label === "Frame" && activeColour.filter;
                  return (
                    <img
                      key={img.src}
                      src={img.src}
                      alt={`${product.name || product.code} — ${img.label}`}
                      data-testid={`product-modal-image-${img.label.toLowerCase()}`}
                      loading="eager"
                      className="absolute inset-0 h-full w-full object-contain p-4 transition-opacity duration-[700ms] ease-[cubic-bezier(0.19,1,0.22,1)] lg:p-8"
                      style={{
                        opacity: i === idx ? 1 : 0,
                        filter: shouldTint ? `url(#${activeColour.filter})` : undefined,
                      }}
                    />
                  );
                })}
              </div>
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    aria-label="Previous image"
                    data-testid="product-modal-prev"
                    onClick={() =>
                      setIdx((i) => (i - 1 + images.length) % images.length)
                    }
                    className="absolute left-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-[#0a0a0c] backdrop-blur transition-all duration-300 hover:bg-[#0a0a0c] hover:text-white"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                      <path
                        d="M15 5l-7 7 7 7"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    aria-label="Next image"
                    data-testid="product-modal-next"
                    onClick={() => setIdx((i) => (i + 1) % images.length)}
                    className="absolute right-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-[#0a0a0c] backdrop-blur transition-all duration-300 hover:bg-[#0a0a0c] hover:text-white"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                      <path
                        d="M9 5l7 7-7 7"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2">
                    {images.map((img, i) => (
                      <button
                        type="button"
                        key={img.src}
                        aria-label={`Show ${img.label}`}
                        data-testid={`product-modal-dot-${i}`}
                        onClick={() => setIdx(i)}
                        className={`h-1.5 w-8 rounded-full transition-colors duration-300 ${
                          i === idx ? "bg-[#0a0a0c]" : "bg-[#0a0a0c]/25"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Right: content + form — natural height, backdrop handles scroll */}
            <div
              data-modal-scroller
              className="flex flex-1 flex-col p-6 lg:max-w-[520px] lg:p-10"
            >
              <ModalCustomNotice />
              <p
                data-testid="product-modal-category"
                className="font-tech text-[10px] uppercase tracking-[0.4em] text-[#c6a87c]"
              >
                {product.categoryLabel}
              </p>
              <h3
                data-testid="product-modal-name"
                className="mt-2 font-serif-display text-3xl font-bold uppercase tracking-tight text-[#0a0a0c] lg:text-4xl"
              >
                {product.name || product.code}
              </h3>
              <span
                aria-hidden
                className="mt-3 block h-[2px] w-14 rounded-full bg-gradient-to-r from-[#c6a87c] to-[#c6a87c]/0"
              />
              <p
                data-testid="product-modal-code"
                className="mt-3 font-tech text-[11px] uppercase tracking-[0.32em] text-[#0a0a0c]/55"
              >
                Model: {product.code}
              </p>

              <p className="mt-5 font-body text-[13px] leading-[1.75] text-[#0a0a0c]/65 lg:text-[14px]">
                {isHardware
                  ? "Precision-cast hardware supplied as pictured — no size or finish configuration required. Enter the quantity you need to receive a tailored quote."
                  : isLuxury
                    ? "One-of-one bespoke external entrance, hand-fabricated to your specification. Choose a hinge or pivot action, swing direction and lock preference — our studio handles the rest."
                    : tintable
                      ? "Bespoke-fabricated thermally-broken steel with slim sight lines, precision-mitred joinery and Ottostumm profiles. Configure width, height, glazing and colour to receive a tailored quote."
                      : "Bespoke-fabricated thermally-broken steel with slim sight lines, precision-mitred joinery and Ottostumm profiles. Configure width, height and glazing to receive a tailored quote."}
              </p>

              {/* Form */}
              {!submitted ? (
                <form
                  data-testid="product-modal-form"
                  onSubmit={submit}
                  noValidate
                  className="mt-8 flex flex-col gap-4"
                >
                  {isHardware ? (
                    <Field label="Quantity">
                      <input
                        type="number"
                        min="1"
                        step="1"
                        required
                        value={form.quantity}
                        onChange={updateField("quantity")}
                        data-testid="quote-quantity"
                        placeholder="1"
                        className={inputCls}
                      />
                    </Field>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex flex-col gap-2">
                          <LabelWithHelp
                            label="Width (mm)"
                            testId="help-width"
                            help="Enter the finished opening width. Standard single doors are 900 mm, doubles 1600–1800 mm, and pivot entrances 1000–1400 mm."
                          />
                          <input
                            type="number"
                            min="1"
                            step="1"
                            required
                            value={form.width_mm}
                            onChange={updateField("width_mm")}
                            data-testid="quote-width"
                            placeholder="2400"
                            className={inputCls}
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <LabelWithHelp
                            label="Height (mm)"
                            testId="help-height"
                            help="Enter finished head height. Standard doors are 2100 mm; feature and pivot doors extend to 2400–3000 mm."
                          />
                          <input
                            type="number"
                            min="1"
                            step="1"
                            required
                            value={form.height_mm}
                            onChange={updateField("height_mm")}
                            data-testid="quote-height"
                            placeholder="2200"
                            className={inputCls}
                          />
                        </div>
                      </div>

                      {/* Need Help Measuring — neon-glow toggle */}
                      <button
                        type="button"
                        data-testid="need-help-measuring-btn"
                        onClick={() => setShowMeasuringGuide((v) => !v)}
                        className="group relative -mt-1 flex w-full items-center justify-between gap-3 rounded-md border border-[#c6a87c] bg-[#0a0a0c] px-4 py-2.5 font-tech text-[10px] uppercase tracking-[0.24em] text-[#c6a87c] transition-all duration-300 hover:bg-[#c6a87c]/10"
                        style={{
                          textShadow:
                            "0 0 6px rgba(198,168,124,0.85), 0 0 12px rgba(198,168,124,0.5)",
                          animation: "neon-pulse 2.6s ease-in-out infinite",
                        }}
                      >
                        <span className="flex items-center gap-2.5">
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden
                          >
                            <path d="M21.3 8.7L15.3 2.7a1 1 0 0 0-1.4 0L2.7 13.9a1 1 0 0 0 0 1.4l6 6a1 1 0 0 0 1.4 0L21.3 10.1a1 1 0 0 0 0-1.4z" />
                            <path d="M7.5 12.5l2 2" />
                            <path d="M10.5 9.5l2 2" />
                            <path d="M13.5 6.5l2 2" />
                            <path d="M4.5 15.5l2 2" />
                          </svg>
                          Need Help Measuring?
                        </span>
                        <span className="text-[16px] leading-none">
                          {showMeasuringGuide ? "−" : "+"}
                        </span>
                      </button>

                      <AnimatePresence initial={false}>
                        {showMeasuringGuide && (
                          <motion.div
                            key="measure-guide"
                            data-testid="measuring-guide-panel"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.45, ease }}
                            className="overflow-hidden"
                          >
                            <div className="mt-3 flex items-center justify-between border-b border-[#c6a87c]/40 pb-2">
                              <p className="font-tech text-[10px] uppercase tracking-[0.28em] text-[#c6a87c]">
                                Eurosteel Measuring Guide · Hinged Doors
                              </p>
                              <a
                                href="/specs/Eurosteel-Measuring-Guide-Hinged-Doors.pdf"
                                target="_blank"
                                rel="noreferrer"
                                data-testid="measuring-guide-open-tab"
                                className="font-tech text-[10px] uppercase tracking-[0.24em] text-[#c6a87c] hover:text-[#e2c69a]"
                              >
                                Open ↗
                              </a>
                            </div>
                            <div className="mt-3 h-[520px] w-full overflow-hidden rounded-sm border border-white/10 bg-white">
                              <iframe
                                title="Eurosteel Measuring Guide"
                                src="/specs/Eurosteel-Measuring-Guide-Hinged-Doors.pdf#toolbar=1&view=FitH"
                                className="h-full w-full"
                                data-testid="measuring-guide-iframe"
                              />
                            </div>
                            <p className="mt-2 font-body text-[11px] leading-relaxed text-white/45">
                              Not sure? Measure the finished opening width and head height in millimetres. If your PDF viewer can't
                              preview inline, use "Open ↗" above.
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {isLuxury && showConfig && (
                        <div
                          data-testid="luxury-config"
                          className="flex flex-col gap-5 rounded-md border border-[#c6a87c]/40 bg-gradient-to-br from-[#c6a87c]/10 via-white to-white p-5"
                        >
                          <p className="font-tech text-[10px] font-bold uppercase tracking-[0.28em] text-[#c6a87c]">
                            Bespoke one-of-one · External
                          </p>

                          <Field label="Action">
                            <div
                              data-testid="luxury-action"
                              className="grid grid-cols-2 gap-2"
                            >
                              {["Hinge", "Pivot"].map((a) => {
                                const active = form.opening === a;
                                return (
                                  <button
                                    key={a}
                                    type="button"
                                    data-testid={`luxury-action-${a.toLowerCase()}`}
                                    onClick={() =>
                                      setForm((f) => ({
                                        ...f,
                                        opening: a,
                                        // Pivot doesn't need a hand (centre-mounted axis)
                                        handing: a === "Pivot" ? "" : f.handing || "Inswing Left",
                                      }))
                                    }
                                    className={`rounded-full border px-4 py-2.5 font-tech text-[11px] font-semibold uppercase tracking-[0.28em] transition-all duration-300 ${
                                      active
                                        ? "border-[#c6a87c] bg-[#0a0a0c] text-[#c6a87c]"
                                        : "border-[#0a0a0c]/20 bg-white text-[#0a0a0c]/70 hover:border-[#c6a87c] hover:text-[#c6a87c]"
                                    }`}
                                  >
                                    {a}
                                  </button>
                                );
                              })}
                            </div>
                          </Field>

                          {form.opening === "Hinge" && (
                            <Field label="Swing direction">
                              <select
                                value={form.handing}
                                onChange={updateField("handing")}
                                data-testid="luxury-swing"
                                className={inputCls}
                              >
                                {[
                                  "Inswing Left",
                                  "Inswing Right",
                                  "Outswing Left",
                                  "Outswing Right",
                                ].map((d) => (
                                  <option key={d} value={d}>
                                    {d}
                                  </option>
                                ))}
                              </select>
                            </Field>
                          )}

                          <Field label="Lock">
                            <div
                              data-testid="luxury-lock"
                              className="grid grid-cols-2 gap-2"
                            >
                              {["Key Lock", "Electronic Hardware"].map((l) => {
                                const active = form.lock === l;
                                return (
                                  <button
                                    key={l}
                                    type="button"
                                    data-testid={`luxury-lock-${l.toLowerCase().replace(/\s+/g, "-")}`}
                                    onClick={() =>
                                      setForm((f) => ({ ...f, lock: l }))
                                    }
                                    className={`rounded-full border px-4 py-2.5 font-tech text-[11px] font-semibold uppercase tracking-[0.28em] transition-all duration-300 ${
                                      active
                                        ? "border-[#c6a87c] bg-[#0a0a0c] text-[#c6a87c]"
                                        : "border-[#0a0a0c]/20 bg-white text-[#0a0a0c]/70 hover:border-[#c6a87c] hover:text-[#c6a87c]"
                                    }`}
                                  >
                                    {l}
                                  </button>
                                );
                              })}
                            </div>
                          </Field>
                        </div>
                      )}

                      {showConfig && !isLuxury && (
                        <div className="border-l-2 border-[#c6a87c] bg-gradient-to-r from-[#c6a87c]/10 to-transparent pl-4 py-2 rounded-r-sm">
                          <Field label="Application">
                            <div
                              data-testid="quote-application"
                              className="grid grid-cols-2 gap-2"
                            >
                              {APPLICATIONS.map((a) => {
                                const active = form.application === a;
                                return (
                                  <button
                                    key={a}
                                    type="button"
                                    data-testid={`quote-application-${a.toLowerCase()}`}
                                    onClick={() =>
                                      setForm((f) => ({ ...f, application: a }))
                                    }
                                    className={`rounded-full border px-4 py-2.5 font-tech text-[11px] font-semibold uppercase tracking-[0.28em] transition-all duration-300 ${
                                      active
                                        ? "border-[#c6a87c] bg-[#0a0a0c] text-[#c6a87c]"
                                        : "border-[#0a0a0c]/20 bg-white text-[#0a0a0c]/70 hover:border-[#c6a87c] hover:text-[#c6a87c]"
                                    }`}
                                  >
                                    {a}
                                  </button>
                                );
                              })}
                            </div>
                          </Field>
                        </div>
                      )}

                      {!isLuxury && (
                        <div className="flex flex-col gap-2">
                          <LabelWithHelp
                            label="Glass type"
                            testId="help-glass"
                            help="Choose how the glazing reads: single or double glazed for full clear vision, Low-E DGU for thermal performance, reeded/fluted for vertical privacy stripes, frosted for total obscure privacy, or Flemish for classic hand-rolled dimpled texture."
                          />
                          <GlassPicker
                            value={form.glass_type}
                            onChange={(g) =>
                              setForm((f) => ({ ...f, glass_type: g }))
                            }
                            options={GLASS_OPTIONS}
                          />
                        </div>
                      )}

                      {showConfig && !isLuxury && (
                        <>
                          <div className="grid grid-cols-2 gap-3 border-t border-[#c6a87c]/20 pt-5">
                            <Field label="Profile">
                              <select
                                value={form.profile}
                                onChange={updateField("profile")}
                                data-testid="quote-profile"
                                className={inputCls}
                              >
                                <option value="">Select profile</option>
                                {filteredProfiles.map((p) => (
                                  <option key={p.value} value={p.value}>
                                    {p.label}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                data-testid="need-help-choosing-profile-btn"
                                onClick={() => setShowProfileHelper((v) => !v)}
                                style={{
                                  textShadow:
                                    "0 0 6px rgba(198,168,124,0.85), 0 0 12px rgba(198,168,124,0.5)",
                                  animation:
                                    "neon-pulse 2.6s ease-in-out infinite",
                                }}
                                className="mt-2 flex w-full items-center justify-between gap-2 rounded-md border border-[#c6a87c] bg-[#0a0a0c] px-3 py-2 font-tech text-[9px] uppercase tracking-[0.22em] text-[#c6a87c] transition-colors hover:bg-[#c6a87c]/10"
                              >
                                <span className="flex items-center gap-2">
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden
                                  >
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                                    <line x1="12" y1="17" x2="12" y2="17" />
                                  </svg>
                                  Need Help Choosing?
                                </span>
                                <span className="text-[14px] leading-none">
                                  {showProfileHelper ? "−" : "+"}
                                </span>
                              </button>
                              <AnimatePresence initial={false}>
                                {showProfileHelper && (
                                  <motion.div
                                    key="profile-helper"
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.45, ease }}
                                    className="col-span-2 overflow-hidden"
                                  >
                                    <ProfileHelper
                                      availableProfiles={filteredProfiles}
                                      onApply={(val) => {
                                        setForm((f) => ({ ...f, profile: val }));
                                        setShowProfileHelper(false);
                                      }}
                                      onClose={() => setShowProfileHelper(false)}
                                    />
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </Field>
                            {openingOpts.length > 0 && (
                              <Field label="Opening type">
                                <select
                                  value={form.opening}
                                  onChange={updateField("opening")}
                                  data-testid="quote-opening"
                                  className={inputCls}
                                >
                                  <option value="">Select opening</option>
                                  {openingOpts.map((o) => (
                                    <option key={o} value={o}>
                                      {o}
                                    </option>
                                  ))}
                                </select>
                              </Field>
                            )}
                          </div>

                          {requiresHanding && (
                            <div className="flex flex-col gap-2">
                              <LabelWithHelp
                                label="Swing direction"
                                testId="help-handing"
                                help="Pick the exact swing. Interior/exterior refers to which side the door opens toward. Left/right = which side the hinges are on when you're standing on the exterior looking in."
                              />
                              <SwingPicker
                                opening={form.opening}
                                handing={form.handing}
                                onChange={({ opening, handing }) =>
                                  setForm((f) => ({ ...f, opening, handing }))
                                }
                              />
                            </div>
                          )}

                          <div className="flex flex-col gap-3 border-t border-[#c6a87c]/20 pt-5">
                            <LabelWithHelp
                              label="Hardware"
                              testId="help-hardware"
                              help={catKey === "steel-windows"
                                ? "Pick your window latch or handle style — espagnolette, monkey-tail, cockspur, casement fastener, tilt-turn — then a finish (Black, Satin, Brass, Bronze). Windows use fixed hardware geometry, so no size options."
                                : "Pick your handle type (lever, pull, T-bar, D-pull, half-moon, etc.), then a detail — either a finish (Black, Satin, Brass) or a size (250mm–1200mm) for pull styles. Hardware is quoted separately."}
                            />
                            <div className="grid grid-cols-2 gap-3">
                              <Field label={catKey === "steel-windows" ? "Window handle / latch" : "Handle type"}>
                                <select
                                  value={form.hardware}
                                  onChange={updateField("hardware")}
                                  data-testid="quote-handle-type"
                                  className={inputCls}
                                >
                                  {handleTypesFor(catKey).map((h) => (
                                    <option key={h} value={h}>
                                      {h}
                                    </option>
                                  ))}
                                </select>
                              </Field>
                              <Field label={catKey === "steel-windows" ? "Finish" : "Handle detail / size"}>
                                <select
                                  value={form.handle_detail}
                                  onChange={updateField("handle_detail")}
                                  data-testid="quote-handle-detail"
                                  className={inputCls}
                                >
                                  {handleDetailsFor(catKey).map((d) => (
                                    <option key={d} value={d}>
                                      {d}
                                    </option>
                                  ))}
                                </select>
                                <input
                                  type="text"
                                  value={form.handle_custom_model || ""}
                                  onChange={updateField("handle_custom_model")}
                                  data-testid="quote-handle-custom-model"
                                  placeholder={catKey === "steel-windows"
                                    ? "Or specify model — e.g. Iver Cast Iron Monkey Tail"
                                    : "Or specify model — e.g. Iver Charleston Long Plate"}
                                  maxLength={120}
                                  className={`${inputCls} mt-2 placeholder:text-[#0a0a0c]/35`}
                                />
                              </Field>
                            </div>
                          </div>

                          <Field label={catKey === "steel-windows" ? "Window lock / latch" : "Lock / latch type"}>
                            <select
                              value={form.lock}
                              onChange={updateField("lock")}
                              data-testid="quote-lock"
                              className={inputCls}
                            >
                              {lockLatchTypesFor(catKey).map((l) => (
                                <option key={l} value={l}>
                                  {l}
                                </option>
                              ))}
                            </select>
                          </Field>

                          <div className="flex flex-col gap-2 rounded-sm border border-[#c6a87c]/40 bg-gradient-to-br from-[#c6a87c]/10 via-[#f6f4ef]/50 to-transparent p-4">
                            <LabelWithHelp
                              label="Electronic Lock (Optional)"
                              testId="help-electronic-lock"
                              help="Add a smart lock to any door. Aqara U400 offers fingerprint + Apple Home; Visage Zeno adds face recognition; Yale Assure Lock 2 pairs with Wi-Fi and keypad. Leave as 'None' to keep the standard lock only."
                            />
                            <Field label="Model">
                              <select
                                value={form.electronic_lock}
                                onChange={updateField("electronic_lock")}
                                data-testid="quote-electronic-lock"
                                className={inputCls}
                              >
                                {ELECTRONIC_LOCK_MODELS.map((m) => (
                                  <option key={m} value={m}>
                                    {m}
                                  </option>
                                ))}
                              </select>
                            </Field>
                          </div>

                          {form.lock === "Other (specify)" && (
                            <Field label="Lock — please specify">
                              <input
                                type="text"
                                value={form.lock_other}
                                onChange={updateField("lock_other")}
                                data-testid="quote-lock-other"
                                placeholder="Brand / model / key-code"
                                className={inputCls}
                              />
                            </Field>
                          )}

                          <div
                            className={`grid gap-3 border-t border-[#c6a87c]/20 pt-5 ${
                              isExternal ? "grid-cols-2" : "grid-cols-1"
                            }`}
                          >
                            <Field label="Fixing type">
                              <select
                                value={form.fixing}
                                onChange={updateField("fixing")}
                                data-testid="quote-fixing"
                                className={inputCls}
                              >
                                <option value="">Select fixing</option>
                                {FIXING_OPTIONS.map((f) => (
                                  <option key={f} value={f}>
                                    {f}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                data-testid="need-help-fixing-btn"
                                onClick={() => setShowFixingHelper((v) => !v)}
                                style={{
                                  textShadow:
                                    "0 0 6px rgba(198,168,124,0.85), 0 0 12px rgba(198,168,124,0.5)",
                                  animation: "neon-pulse 2.6s ease-in-out infinite",
                                }}
                                className="mt-2 flex w-full items-center justify-between gap-2 rounded-md border border-[#c6a87c] bg-[#0a0a0c] px-3 py-2 font-tech text-[9px] uppercase tracking-[0.22em] text-[#c6a87c] transition-colors hover:bg-[#c6a87c]/10"
                              >
                                <span className="flex items-center gap-2">
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden
                                  >
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                                    <line x1="12" y1="17" x2="12" y2="17" />
                                  </svg>
                                  Need Help Choosing?
                                </span>
                                <span className="text-[14px] leading-none">
                                  {showFixingHelper ? "−" : "+"}
                                </span>
                              </button>
                              <AnimatePresence initial={false}>
                                {showFixingHelper && (
                                  <motion.div
                                    key="fixing-helper"
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.45, ease }}
                                    className="overflow-hidden"
                                  >
                                    <FixingHelper
                                      currentApplication={form.application}
                                      onApply={(val) => {
                                        setForm((f) => ({ ...f, fixing: val }));
                                        setShowFixingHelper(false);
                                      }}
                                      onClose={() => setShowFixingHelper(false)}
                                    />
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </Field>
                            {isExternal && (
                              <Field label="Threshold">
                                <select
                                  value={form.threshold}
                                  onChange={updateField("threshold")}
                                  data-testid="quote-threshold"
                                  className={inputCls}
                                >
                                  <option value="">Select threshold</option>
                                  {THRESHOLD_OPTIONS.map((t) => (
                                    <option key={t} value={t}>
                                      {t}
                                    </option>
                                  ))}
                                </select>
                              </Field>
                            )}
                          </div>

                          {catKey === "luxury-entrances" && (
                            <p
                              data-testid="luxury-panel-note"
                              className="rounded-sm border border-[#c6a87c]/40 bg-[#c6a87c]/10 px-3 py-2 font-tech text-[10px] font-semibold uppercase tracking-[0.25em] text-[#8a6a3d]"
                            >
                              Panel: {LUXURY_PANEL_CONSTRUCTION} — hinge / pivot only
                            </p>
                          )}
                          {!isExternal && form.application && (
                            <p className="font-tech text-[10px] font-semibold uppercase tracking-[0.25em] text-[#0a0a0c]/60">
                              Internal — no threshold across floor
                            </p>
                          )}
                        </>
                      )}

                      {tintable && (
                        <>
                          <Field label={`Colour — ${activeColour.label}`}>
                            <ColourSwatches
                              selected={colourKey}
                              onChange={handleColourPick}
                              size={22}
                              testIdPrefix="swatch-quote-colour"
                            />
                          </Field>

                          {activeColour.label === "Custom" && (
                            <Field label="Custom colour details">
                              <input
                                type="text"
                                value={form.custom_colour}
                                onChange={updateField("custom_colour")}
                                data-testid="quote-custom-colour"
                                placeholder="RAL 7016 Anthracite, textured black, etc."
                                className={inputCls}
                              />
                            </Field>
                          )}
                        </>
                      )}
                    </>
                  )}

                  <Field label="Notes (optional)">
                    <textarea
                      value={form.notes}
                      onChange={updateField("notes")}
                      data-testid="quote-notes"
                      rows={3}
                      placeholder={
                        isHardware
                          ? "Project name, installation address, delivery timeframe…"
                          : "Site location, install timeframe, hardware preferences…"
                      }
                      className={`${inputCls} resize-none`}
                    />
                  </Field>

                  <Field label="Email">
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={updateField("email")}
                      data-testid="quote-email"
                      placeholder="you@company.com"
                      className={inputCls}
                    />
                  </Field>

                  <Field label="Phone (optional)">
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={updateField("phone")}
                      data-testid="quote-phone"
                      placeholder="e.g. 021 234 5678"
                      autoComplete="tel"
                      className={inputCls}
                    />
                  </Field>

                  {error && (
                    <p
                      data-testid="quote-error"
                      className="font-tech text-[11px] uppercase tracking-[0.25em] text-red-600"
                    >
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="quote-submit"
                    className="group relative mt-2 inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-full bg-[#0a0a0c] px-8 py-4 font-tech text-[11px] uppercase tracking-[0.35em] text-white shadow-[0_0_0_1px_#c6a87c66] transition-all duration-500 hover:bg-[#c6a87c] hover:text-[#0a0a0c] hover:shadow-[0_0_0_1px_#c6a87c,0_10px_28px_-8px_#c6a87caa] disabled:opacity-60"
                  >
                    <span className="h-1 w-1 rounded-full bg-[#c6a87c] transition-colors group-hover:bg-[#0a0a0c]" />
                    {submitting ? "Sending…" : "Get a Quote"}
                    <span className="h-1 w-1 rounded-full bg-[#c6a87c] transition-colors group-hover:bg-[#0a0a0c]" />
                  </button>
                </form>
              ) : (
                <div
                  data-testid="quote-success"
                  className="mt-8 border border-[#c6a87c]/40 bg-[#c6a87c]/8 p-6 text-center"
                >
                  <p className="font-tech text-[10px] uppercase tracking-[0.4em] text-[#c6a87c]">
                    Quote request received
                  </p>
                  <h4 className="mt-3 font-serif-display text-2xl font-medium tracking-tight text-[#0a0a0c]">
                    Thank you.
                  </h4>
                  <p className="mt-3 font-body text-[13px] leading-[1.7] text-[#0a0a0c]/70">
                    A Eurosteel specialist will review your requirements for
                    <span className="font-medium"> {product.code}</span> and
                    respond to <span className="font-medium">{form.email}</span> shortly.
                  </p>
                  <button
                    type="button"
                    onClick={onClose}
                    data-testid="quote-success-close"
                    className="mt-6 inline-flex items-center justify-center rounded-full border border-[#0a0a0c] px-8 py-3 font-tech text-[10px] uppercase tracking-[0.35em] text-[#0a0a0c] transition-all duration-500 hover:bg-[#0a0a0c] hover:text-white"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const inputCls =
  "w-full border border-[#0a0a0c]/15 bg-white px-4 py-3 font-body text-[13px] text-[#0a0a0c] placeholder:text-[#0a0a0c]/35 outline-none transition-colors duration-300 focus:border-[#0a0a0c]";

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="flex items-center gap-2 font-tech text-[10px] font-semibold uppercase tracking-[0.32em] text-[#0a0a0c]">
        <span aria-hidden className="h-[10px] w-[3px] rounded-full bg-[#c6a87c]" />
        {label}
      </span>
      {children}
    </label>
  );
}
